/**
 * Historical Data Import — import workout history from other apps.
 *
 * Supports:
 * - Hevy CSV export
 * - Strong CSV export
 * - Generic CSV (date, exercise, weight, reps, sets)
 *
 * Revenue path: removes the #1 switching cost. Hevy users who bring their
 * history are 3x more likely to subscribe to Pro.
 */

import { supabase } from './supabase';
import { normalizeToV1Lift } from './lifts/canonical';
import { createLogger } from './logger';

const EXERCISE_NAME_MAP: Record<string, string> = {
  'barbell bench press': 'Bench Press',
  'barbell back squat': 'Back Squat',
  'barbell squat': 'Back Squat',
  'back squat': 'Back Squat',
  'barbell deadlift': 'Deadlift',
  'conventional deadlift': 'Deadlift',
  'weighted pull-up': 'Weighted Pull-Up',
  'pull-up': 'Weighted Pull-Up',
  'pullup': 'Weighted Pull-Up',
  'dead hang': 'Dead Hang',
  'deadhang': 'Dead Hang',
  'overhead press': 'Overhead Press',
  'barbell overhead press': 'Overhead Press',
  'barbell row': 'Barbell Row',
  'bent over row': 'Barbell Row',
};

function normalizeExerciseName(raw: string): string {
  const lower = raw.toLowerCase().trim();
  return EXERCISE_NAME_MAP[lower] ?? raw;
}

const log = createLogger('DATA_IMPORT');

export interface ImportRow {
  date: string;
  exercise: string;
  weight: number;
  reps: number;
  sets: number;
  unit: string;
}

export interface ImportResult {
  totalRows: number;
  imported: number;
  skipped: number;
  errors: string[];
  v1LiftsFound: string[];
}

/**
 * Parse a Hevy CSV export into ImportRows.
 * Hevy CSV format: title, start_time, end_time, exercise_name, set_order, weight, reps, ...
 */
export function parseHevyCSV(csvText: string): ImportRow[] {
  const lines = csvText.split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];

  const header = lines[0].toLowerCase();
  const isHevy = header.includes('exercise_name') || header.includes('set_order');

  if (!isHevy) return parseGenericCSV(csvText);

  const rows: ImportRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',').map(c => c.trim().replace(/"/g, ''));
    // Hevy columns: title(0), start_time(1), end_time(2), exercise_name(3), set_order(4), weight(5), reps(6)
    const date = cols[1] ?? '';
    const exercise = cols[3] ?? '';
    const weight = parseFloat(cols[5]) || 0;
    const reps = parseInt(cols[6]) || 0;

    if (exercise && (weight > 0 || reps > 0)) {
      rows.push({ date, exercise, weight, reps, sets: 1, unit: 'lbs' });
    }
  }
  return rows;
}

/**
 * Parse a Strong CSV export.
 * Strong CSV format: Date, Workout Name, Exercise Name, Set Order, Weight, Reps, ...
 */
export function parseStrongCSV(csvText: string): ImportRow[] {
  const lines = csvText.split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];

  const rows: ImportRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',').map(c => c.trim().replace(/"/g, ''));
    const date = cols[0] ?? '';
    const exercise = cols[2] ?? '';
    const weight = parseFloat(cols[4]) || 0;
    const reps = parseInt(cols[5]) || 0;

    if (exercise && (weight > 0 || reps > 0)) {
      rows.push({ date, exercise, weight, reps, sets: 1, unit: 'lbs' });
    }
  }
  return rows;
}

/**
 * Parse a generic CSV (date, exercise, weight, reps).
 */
export function parseGenericCSV(csvText: string): ImportRow[] {
  const lines = csvText.split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];

  const rows: ImportRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',').map(c => c.trim().replace(/"/g, ''));
    rows.push({
      date: cols[0] ?? '',
      exercise: cols[1] ?? '',
      weight: parseFloat(cols[2]) || 0,
      reps: parseInt(cols[3]) || 0,
      sets: parseInt(cols[4]) || 1,
      unit: cols[5] || 'lbs',
    });
  }
  return rows;
}

/**
 * Auto-detect CSV format and parse.
 */
export function parseCSV(csvText: string): ImportRow[] {
  const header = csvText.split('\n')[0]?.toLowerCase() ?? '';
  if (header.includes('exercise_name') || header.includes('set_order')) {
    return parseHevyCSV(csvText);
  }
  if (header.includes('workout name')) {
    return parseStrongCSV(csvText);
  }
  return parseGenericCSV(csvText);
}

/**
 * Import parsed rows into the user's workout history.
 * Groups by date, creates one workout per day, inserts strength events for V1 lifts.
 */
export async function importWorkoutHistory(
  userId: string,
  rows: ImportRow[],
  profileBodyweight: number,
  profileUnits: string,
): Promise<ImportResult> {
  const result: ImportResult = {
    totalRows: rows.length,
    imported: 0,
    skipped: 0,
    errors: [],
    v1LiftsFound: [],
  };

  // Group by date
  const byDate = new Map<string, ImportRow[]>();
  for (const row of rows) {
    const dateKey = row.date.slice(0, 10);
    if (!byDate.has(dateKey)) byDate.set(dateKey, []);
    byDate.get(dateKey)!.push(row);
  }

  const v1Set = new Set<string>();

  for (const [dateKey, dayRows] of byDate) {
    try {
      // Build workout payload
      const exercises: any[] = [];
      for (const row of dayRows) {
        // Normalize exercise name: check static map first, then V1 canonical
        const mappedName = normalizeExerciseName(row.exercise);
        const v1Key = normalizeToV1Lift(mappedName) ?? normalizeToV1Lift(row.exercise);
        const normalizedName = v1Key
          ? v1Key.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
          : mappedName;

        exercises.push({
          name: normalizedName,
          sets: [{ weight: row.weight, reps: row.reps, is_pr: false }],
          notes: null,
          rpe: null,
          equipment: null,
          supersetGroupId: null,
          is_competition: false,
        });

        // Check if V1 lift (check original and normalized)
        if (v1Key) v1Set.add(v1Key);
      }

      const payload = {
        exercises,
        units: profileUnits,
        workoutName: `Imported — ${dateKey}`,
        sessionNotes: 'Imported from previous app',
        supersetGroups: null,
      };

      // Insert workout
      const { error } = await supabase
        .from('workouts')
        .insert({
          user_id: userId,
          performed_at: new Date(dateKey).toISOString(),
          payload,
        });

      if (error) {
        result.errors.push(`${dateKey}: ${error.message}`);
        result.skipped += dayRows.length;
      } else {
        result.imported += dayRows.length;
      }
    } catch (err: any) {
      result.errors.push(`${dateKey}: ${err.message}`);
      result.skipped += dayRows.length;
    }
  }

  result.v1LiftsFound = [...v1Set];
  log.info('Import complete:', result);
  return result;
}
